import ColorPicker from "components/colorPicker/ColorPicker";
import RadioGroup from "components/radioGroup/RadioGroup";
import React, { useState } from "react";
import Modal from "react-modal";
import {
  bulkEditParse,
  bulkEditStringify,
  cssVars,
  defaultDarkVars,
  defaultLightVars,
  themeOptions,
  Theme,
  ThemeSettingsValue,
  validateVars,
  CssVars,
} from "./themeSettingsUtils";
import "./ThemeSettingsModal.scss";

type ThemeSettingsModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  value: ThemeSettingsValue;
  onChange: (value: ThemeSettingsValue) => void;
  infoMessage?: string;
};

const ThemeSettingsModal = ({
  isOpen,
  onRequestClose,
  value,
  onChange,
  infoMessage,
}: ThemeSettingsModalProps) => {
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkEditText, setBulkEditText] = useState("");
  const [bulkEditErrors, setBulkEditErrors] = useState("");

  const saveBulkEdit = () => {
    const newVars = bulkEditParse(bulkEditText);
    const errors = validateVars(newVars);
    if (errors) {
      setBulkEditErrors(errors);
      return;
    }

    setBulkEditErrors("");
    onChange({
      ...value,
      customTheme: newVars as CssVars,
    });
    setBulkEditMode(false);
  };

  const onRadioSelect = (selectedTheme: Theme) => {
    onChange({
      ...value,
      theme: selectedTheme,
    });
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      contentLabel="Theme Settings"
      onRequestClose={onRequestClose}
      className={{
        base: "modal-base",
        afterOpen: "",
        beforeClose: "",
      }}
      overlayClassName={{
        base: "modal-overlay",
        afterOpen: "",
        beforeClose: "",
      }}
    >
      <div className="content" style={{ minWidth: 300 }}>
        {!!infoMessage && (
          <div className="theme-settings-info-message">{infoMessage}</div>
        )}
        {bulkEditMode ? (
          <div>
            <textarea
              value={bulkEditText}
              style={{ height: 240 }}
              onChange={(event) => {
                setBulkEditErrors("");
                setBulkEditText(event.target.value);
              }}
            />
          </div>
        ) : (
          <>
            <RadioGroup
              onCheck={onRadioSelect}
              value={value.theme ?? "dark"}
              items={themeOptions}
            />
            {value.theme === "custom" && (
              <>
                <div style={{ marginBottom: 20 }}>
                  {cssVars.map((cssVar) => {
                    return (
                      <div key={cssVar}>
                        <label
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <div style={{ minWidth: 160 }}>{cssVar}: </div>
                          <ColorPicker
                            onChange={(nextColor) => {
                              if (nextColor) {
                                onChange({
                                  ...value,
                                  customTheme: {
                                    ...value.customTheme,
                                    [cssVar]: nextColor,
                                  },
                                });
                              }
                            }}
                            value={value.customTheme[cssVar]}
                            validate={(nextColor) => {
                              const proposedVars = {
                                ...value.customTheme,
                                [cssVar]: nextColor,
                              };
                              return validateVars(proposedVars);
                            }}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <button
                    onClick={() => {
                      onChange({
                        ...value,
                        customTheme: defaultDarkVars,
                      });
                    }}
                  >
                    Reset To Dark Mode Defaults
                  </button>
                  <button
                    style={{ marginTop: 12 }}
                    onClick={() => {
                      onChange({
                        ...value,
                        customTheme: defaultLightVars,
                      });
                    }}
                  >
                    Reset To Light Mode Defaults
                  </button>
                  <button
                    style={{ marginTop: 12 }}
                    onClick={() => {
                      setBulkEditText(bulkEditStringify(value.customTheme));
                      setBulkEditMode(true);
                    }}
                  >
                    Bulk Edit Mode
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="button-row">
        {bulkEditMode ? (
          <>
            <button
              style={{ marginRight: 12 }}
              onClick={() => {
                setBulkEditErrors("");
                setBulkEditMode(false);
              }}
            >
              Cancel
            </button>
            <button onClick={saveBulkEdit}>Save</button>
          </>
        ) : (
          <button onClick={onRequestClose}>Close</button>
        )}
      </div>
      {value.theme === "custom" && (
        <div
          style={{
            maxWidth: 300,
            minHeight: 100,
            padding: 12,
          }}
        >
          {!!bulkEditErrors && (
            <div
              style={{
                backgroundColor: "black",
                borderRadius: 4,
                color: "red",
                maxWidth: 300,
                padding: 12,
              }}
            >
              {bulkEditErrors}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ThemeSettingsModal;
